import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Notification from './components/Notification';
import RootRouters from './routers/RootRouters';
import { useAppSelector } from './store/hooks';
import { PossibleHostType } from './interfaces';
import { setHost } from './features/sliceJob';
import Layout from './containers/Layout';
import { useGetUserInfoQuery } from './services/serviceUserInfo';
import { hsIdentify, hsTrackPage } from './utils/hubspot';

function App() {
  const { data: userInfo, isLoading } = useGetUserInfoQuery();
  const { showNotification } = useAppSelector(({ notification }) => ({
    showNotification: notification.show
  }));
  const dispatch = useDispatch();
  const { hostname, port, host } = window.location;
  const { pathname: locationPathname, search: locationSearch } = useLocation();

  useEffect(() => {
    if (host) {
      dispatch(setHost(host as PossibleHostType));
    }
  }, [locationPathname, hostname, port]);

  // HubSpot SPA route tracking: the tracking code (loaded via GTM-K4WKF75L)
  // only auto-tracks the landing page. Fire a virtual pageview on every route
  // change EXCEPT the first (the loader already tracked the landing page —
  // tracking it here too would double-count it).
  const lastHsPath = useRef<string | null>(null);
  useEffect(() => {
    const path = `${locationPathname}${locationSearch}`;
    if (lastHsPath.current === null) {
      lastHsPath.current = path; // landing page: the HubSpot loader tracked it
      return;
    }
    if (lastHsPath.current === path) return;
    lastHsPath.current = path;
    hsTrackPage(path);
  }, [locationPathname, locationSearch]);

  // Tie a logged-in session to its HubSpot contact, once per page lifetime. No
  // explicit flush: it rides the next tracked pageview (route tracking above).
  const hsIdentified = useRef(false);
  useEffect(() => {
    if (hsIdentified.current || !userInfo?.isLoggedIn || !userInfo.email) return;
    hsIdentified.current = true;
    hsIdentify(userInfo.email, {
      firstname: userInfo.firstName || undefined,
      lastname: userInfo.lastName || undefined,
    });
  }, [userInfo]);

  return (
    <>
      {showNotification && <Notification />}
      <Layout
        hiddenContentWhenIsLoading
        isLoading={isLoading}
        noMenu
      >
        <RootRouters />
      </Layout>

    </>
  );
}

export default App;
